import React, { Children, useCallback, useRef, useState } from "react";
import { createEditor, Editor, Transforms } from "slate";
import { Editable, ReactEditor, RenderElementProps, Slate, useFocused, useSelected, useSlateStatic, withReact } from "slate-react";
import SlateEditorTools from "./SlateEditorTools";
import { NXAlignCenter, NXAlignLeft, NXAlignRight, NXDelete, NXImage, NXTablerLink } from "../../icons";
import NXUnorderedList from "../../icons/NXUnorderedList";
import ImageChooseModal from "../images/modal";
import { InputField } from "../common";


interface CustomElement {
  type: "paragraph" | "code";
  children: { text: string }[];
}

interface Props {
  value?: any;
  onChange: (value: any) => void;
}

const SlateEditor = ({
  value = [
    { type: "paragraph", children: [{ text: "" }] },
    // { type: "code", children: [{ text: "This is a code block." }] },
    // { type: "bullet", children: [{ text: "This is a bullet item." }] },
  ],

  onChange,
}: Props) => {
  const [editor] = useState(() => withReact(createEditor()));
  const [isFocused, setIsFocused] = useState(false);
  const [textColor, setTextColor] = useState<any>()
  const [bgColor, setBgColor] = useState<any>()
  const [fontSize, setFontSize] = useState<number>()
  const [isOpenImageModal, setIsOpenImageModal] = useState<boolean>(false)

  const inputRef = useRef<HTMLInputElement>(null);



  const Image = ({ attributes, children, element }: any) => {
    const editor:any = useSlateStatic()
    const path = ReactEditor.findPath(editor, element)
  
    const selected = useSelected()
    const focused = useFocused()
    const [isHovered, setIsHovered] = useState(false);

    const handleClick = () => {
      if (!selected && !focused) {
        Transforms.select(editor, path); // Programmatically select the image if not already selected
      }
    };
    return (
      <div {...attributes}
        onClick={handleClick}
        onMouseEnter={() => setIsHovered(true)} // Show delete button on hover
        onMouseLeave={() => setIsHovered(false)} 
      >
        {children}
        <div
          contentEditable={false}
          className="relative"
        >
          <img
            src={element?.url}
            className={`block ${selected && focused && 'shadow-md'}`}
          />
          <button
            onClick={() => Transforms.removeNodes(editor, { at: path })}
            className={`${selected || isHovered ? 'inline': ''} absolute top-[0.5em] left-[0.5em] p-1 rounded-lg bg-white`}
          >
            <NXDelete className="text-red-600 w-6 h-6"/>
          </button>
        </div>
      </div>
    )
  }

  
  const renderElement = useCallback((props: any) => {
    const style: React.CSSProperties = {};

    if (props.element.align) {
      style.textAlign = props.element.align;
    }

    switch (props.element.type) {
      
      case "h1":
        return (
          <h1 {...props.attributes} style={style} className="text-3xl">
            {props.children}
          </h1>
        );
      case "h2":
        return (
          <h2 {...props.attributes} style={style} className="text-2xl">
            {props.children}
          </h2>
        );
      case "code":
        return (
          <pre {...props.attributes} style={style}>
            <code>{props.children}</code>
          </pre>
        );
      case "bullet":
        return (
          <ul {...props.attributes} className="ml-8">
            <li style={style}>{props.children}</li>
          </ul>
        );

      case "orderedList":
        return (
          <ol {...props.attributes} style={style} className="ml-8">
            <li>{props.children}</li>
          </ol>
        );

      case "image" :
        return (
          <Image {...props}/>
        );

      case "link":
        return (
          <a {...props.attributes} href={props.element.url} target="_blank" rel="noopener noreferrer" style={{ color: 'blue', textDecoration: 'underline'}}>
            {props.children}
          </a>
        )

      default:
        return (
          <p {...props.attributes} style={style}>
            {props.children}
          </p>
        );
    }
  }, []);

  const Leaf = ({ attributes, children, leaf }: any) => {
    let style = {}

    if (leaf.bold) {
      children = <strong>{children}</strong>;
    }
    if (leaf.italic) {
      children = <em>{children}</em>;
    }
    if (leaf.underline) {
      children = (
        <span style={{ textDecoration: "underline" }}>{children}</span>
      );
    }

    if(leaf.color){
      setTextColor(leaf?.color)
    }

    if(leaf.backgroundColor){
      setBgColor(leaf?.backgroundColor)
    }


    if(leaf.fontSize){
      setFontSize(leaf.fontSize)
    }

    return (
      <span 
        {...attributes} 
        style={{ 
          fontSize: leaf.fontSize ? `${leaf.fontSize}px` : 'inherit', 
          color: leaf.color || 'inherit',
          backgroundColor: leaf.backgroundColor || 'inherit', 
        }} 
      >
        {children}
      </span>
    )
  };

  const useFontSize = Array.from(
    { length: Math.floor(96 / 2) + 1 },
    (_, i) => 6 + i * 2
  );

  const handleLink = (editor:any) => {
    const url = prompt("Enter the URL:");
    if (url) {
      SlateEditorTools.toggleLink(editor, url);
    }
  }

  const onSelectedSave = (data: any[]) => {
    data?.map((item)=>(SlateEditorTools.toggleImageUpload(editor, item.image)))
    data=[]
  }

  return (
    <div>
      <Slate
        editor={editor}
        initialValue={value}
        onChange={(value) => onChange(value)}
      >
        <div className={`flex flex-wrap items-center gap-4 border-x-2 border-t-2  py-3 px-5 rounded-tl-lg rounded-tr-lg ${isFocused ? 'border-[#0CAF60] border-solid' : 'border-dashed'}`}>
          
          <span
            className={`cursor-pointer hover:bg-base-100 aspect-square ${SlateEditorTools.isBoldMarkActive(editor) && 'bg-base-100 duration-100'} w-7 h-7 flex justify-center items-center border rounded-md duration-100`}
            onMouseDown={(event: any) => {
              event.preventDefault();
              SlateEditorTools.toggleBoldMark(editor);
            }}
          >
            B
          </span>

          <span
            className={`cursor-pointer italic hover:bg-base-100 aspect-square ${SlateEditorTools.isItalicMarkActive(editor) && 'bg-base-100 duration-100'} w-7 h-7 flex justify-center items-center border rounded-md duration-100`}
            onMouseDown={(event: any) => {
              event.preventDefault();
              SlateEditorTools.toggleItalicMark(editor);
            }}
          >          
            I
          </span>

          <span
            className={`cursor-pointer underline hover:bg-base-100 aspect-square ${SlateEditorTools.isUnderlineMarkActive(editor) && 'bg-base-100 duration-100'} w-7 h-7 flex justify-center items-center border rounded-md duration-100`}
            onMouseDown={(event: any) => {
              event.preventDefault();
              SlateEditorTools.toggleUnderlineMark(editor);
            }}
          >
            U
          </span>
          <span
            className="cursor-pointer"
         
          >
            <label htmlFor="fontSize" className="mr-2">Font</label>
            <select id="fontSize" name="fontSize" value={fontSize} onChange={(e)=>SlateEditorTools.toggleFontSize(editor, e.target.value)}>
              {useFontSize.map((font, index)=>(
                <option key={index} value={font}>{font}</option>
              ))}           
            </select>
          </span>
          <span
            className="cursor-pointer font-bold relative"
          >
            <label 
              htmlFor="text-color" 
              className={`cursor-pointer`} 
              style={{color: textColor}}
            >
              <span className={`hover:bg-base-100 aspect-square w-7 h-7 flex justify-center items-center border rounded-md duration-100`}>
                A
              </span>
            </label>
            <input
              type="color" 
              id="text-color" 
              className=" w-0 h-0 top-8 overflow-hidden absolute" 
              onChange={(e)=>SlateEditorTools.toggleTextColor(editor, e.target.value)}
            />
          </span>
          <span
            className="cursor-pointer relative"
          >
            <label 
              htmlFor="text-bg-color"               
            >
              <span
                style={{backgroundColor: bgColor, color: textColor}}
                className={`bg-black text-white hover:bg-base-100 aspect-square w-7 h-7 flex justify-center items-center border rounded-md duration-100`}
              >
                A
              </span>
            </label>
            <input
              type="color" 
              id="text-bg-color" 
              className=" w-0 h-0 top-8 overflow-hidden absolute" 
              onChange={(e)=>SlateEditorTools.toggleBgColor(editor, e.target.value)}
            />
          </span>

          <span
            className={`cursor-pointer hover:bg-base-100 aspect-square ${SlateEditorTools.isHeading1Active(editor) && 'bg-base-100 duration-100'} w-7 h-7 flex justify-center items-center border rounded-md duration-100 `}
            onMouseDown={(event: any) => {
              event.preventDefault();
              SlateEditorTools.toggleHeading1(editor);
            }}
          >
            H1
          </span>
          <span
            className={`cursor-pointer hover:bg-base-100 aspect-square ${SlateEditorTools.isHeading2Active(editor) && 'bg-base-100 duration-100'} w-7 h-7 flex justify-center items-center border rounded-md duration-100`}
            onMouseDown={(event: any) => {
              event.preventDefault();
              SlateEditorTools.toggleHeading2(editor);
            }}
          >
            H2
          </span>
          <span
            className={`cursor-pointer hover:bg-base-100 aspect-square ${SlateEditorTools.isBulletActive(editor) && 'bg-base-100 duration-100'} w-7 h-7 flex justify-center items-center border rounded-md duration-100`}
            onMouseDown={(event: any) => {
              event.preventDefault();
              SlateEditorTools.toggleBulletBlock(editor);
            }}
          >
            <NXUnorderedList className="w-[16px] h-[16px]"/>
          </span>
          <span
            className={`cursor-pointer hover:bg-base-100 aspect-square ${SlateEditorTools.isNumberBlock(editor) && 'bg-base-100 duration-100'} w-7 h-7 flex justify-center items-center border rounded-md duration-100`}
            onMouseDown={(event: any) => {
              event.preventDefault();
              SlateEditorTools.toggleNumberBlock(editor);
            }}
          >
            N
          </span>
          <span
            className={`cursor-pointer hover:bg-base-100 aspect-square ${SlateEditorTools.isCodeActive(editor) && 'bg-base-100 duration-100'} w-7 h-7 flex justify-center items-center border rounded-md duration-100`}
            onMouseDown={(event: any) => {
              event.preventDefault();
              SlateEditorTools.toggleCodeBlock(editor);
            }}
          >
            {'<>'}
          </span>
          <span
            className={`cursor-pointer hover:bg-base-100 aspect-square w-7 h-7 flex justify-center items-center border rounded-md duration-100`}
            >
            <label htmlFor="editor-image" onClick={()=>setIsOpenImageModal(true)}><NXImage/></label>
            {/* <input 
              id="editor-image" 
              type="file" 
              onChange={(e)=>{
                const images = e.target.files
                if(images){
                  SlateEditorTools.toggleImageUpload(editor, URL.createObjectURL(images[0]) )
                  e.target.files = null
                }
              }} 
              className="hidden"/> */}
              <ImageChooseModal isOpen={isOpenImageModal} onClose={()=>setIsOpenImageModal(!isOpenImageModal)} isMull onSelectedSave={onSelectedSave}/>
          </span>
          <span
            className={`cursor-pointer hover:bg-base-100 aspect-square ${SlateEditorTools.isLeftActive(editor) && 'bg-base-100 duration-100'} w-7 h-7 flex justify-center items-center border rounded-md duration-100`}
            onMouseDown={(event: any) => {
              event.preventDefault();
              SlateEditorTools.toggleAlignment(editor, "left");
            }}
          >
            <NXAlignLeft/>
          </span>
          <span
            className={`cursor-pointer hover:bg-base-100 aspect-square ${SlateEditorTools.isCenterActive(editor) && 'bg-base-100 duration-100'} w-7 h-7 flex justify-center items-center border rounded-md duration-100`}
            onMouseDown={(event: any) => {
              event.preventDefault();
              SlateEditorTools.toggleAlignment(editor, "center");
            }}
          >
            <NXAlignCenter/>
          </span>
          <span
            className={`cursor-pointer hover:bg-base-100 aspect-square ${SlateEditorTools.isRightActive(editor) && 'bg-base-100 duration-100'} w-7 h-7 flex justify-center items-center border rounded-md duration-100`}
            onMouseDown={(event: any) => {
              event.preventDefault();
              SlateEditorTools.toggleAlignment(editor, "right");
            }}
          >
            <NXAlignRight/>
          </span>
          <span
            className={`cursor-pointer hover:bg-base-100 aspect-square ${SlateEditorTools.isJustifyActive(editor) && 'bg-base-100 duration-100'} w-7 h-7 flex justify-center items-center border rounded-md duration-100`}
            onMouseDown={(event: any) => {
              event.preventDefault();
              SlateEditorTools.toggleAlignment(editor, "justify");
            }}
          >
            J
          </span>
          <span
            className={`cursor-pointer hover:bg-base-100 aspect-square ${SlateEditorTools.isLinkActive(editor) && 'bg-base-100 duration-100'} w-7 h-7 flex justify-center items-center border rounded-md duration-100`}
            onClick={(event: any) => {
              event.preventDefault();
              handleLink(editor)
            }}
          >
            <NXTablerLink className="w-[20px] h-[20px]"/>
          </span>
         
        </div>

        <Editable
          ref={inputRef}
          onFocus={()=>setIsFocused(true)}
          onBlur={()=>setIsFocused(false)}
          className="px-5 py-3 border-2 border-dashed rounded-bl-lg rounded-br-lg ring-0 focus:ring-0 focus:outline-none box-border focus:border-[#0CAF60] focus:border-2 focus:border-solid h-[400px] overflow-y-auto"
          renderElement={renderElement}
          renderLeaf={Leaf}
          spellCheck
          onKeyDown={(event) => {
            if (!event.ctrlKey) {
              return;
            }
            

            switch (event.key) {
              case "Enter": {
                const [match]: any = Editor.nodes(editor, {
                  match: (n: any) => n.type === 'image', // check if it's an image node
                });
            
                if (match) {
                  event.preventDefault(); // Prevent default behavior (inserting another image)
            
                  // Insert a new paragraph below the image node (creating space for typing)
                  const paragraph = {
                    type: 'paragraph',
                    children: [{ text: '' }],
                  }
                  Transforms.insertNodes<any>(editor, paragraph);
                }
                break
              }
              

              case "`": {
                event.preventDefault();
                SlateEditorTools.toggleCodeBlock(editor);
                break;
              }

              case "b": {
                event.preventDefault();
                SlateEditorTools.toggleBoldMark(editor);
                break;
              }

              case "i": {
                event.preventDefault();
                SlateEditorTools.toggleBoldMark(editor);
                break;
              }

              case "u": {
                event.preventDefault();
                SlateEditorTools.toggleUnderlineMark(editor);
                break;
              }
            }
          }}
        />
      </Slate>
    </div>
  );
};

export default SlateEditor;
